import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Plus, Trophy, Target } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Loader } from "../components/common";

const ACHIEVEMENTS = [
  { icon: Trophy, label: "12 Workouts Completed" },
  { icon: Target, label: "3-Week Streak" },
  { icon: Trophy, label: "New Squat PR" },
];

export default function Progress() {
  const [measurements, setMeasurements] = useState(null);
  const [goals, setGoals] = useState([]);
  const [newWeight, setNewWeight] = useState("");

  const load = () => {
    api.get("/customer/measurements").then(({ data }) => setMeasurements(data));
    api.get("/customer/goals").then(({ data }) => setGoals(data));
  };
  useEffect(load, []);
  if (!measurements) return <Loader />;

  const weightData = measurements.filter((m) => m.type === "weight").map((m) => ({ date: m.date.slice(5), value: m.value }));
  const latest = weightData[weightData.length - 1]?.value;
  const first = weightData[0]?.value;
  const change = latest && first ? (latest - first).toFixed(1) : 0;

  const addWeight = async () => {
    if (!newWeight) return;
    await api.post("/customer/measurements", { type: "weight", value: Number(newWeight), unit: "lbs" });
    setNewWeight(""); toast.success("Weight logged!"); load();
  };

  return (
    <div className="p-5">
      <h1 className="font-heading text-4xl">Progress</h1>
      <p className="text-secondary text-sm mt-1">Track the trends that matter.</p>

      <div className="card-surface rounded-2xl p-5 mt-5">
        <div className="flex items-end justify-between">
          <div><p className="label-xs">Current Weight</p><p className="font-heading text-5xl text-brand">{latest ?? "—"}<span className="text-xl text-secondary">lbs</span></p></div>
          <div className="text-right"><p className="label-xs">8-week change</p><p className={`font-num text-lg ${change <= 0 ? "text-success" : "text-warning"}`}>{change > 0 ? "+" : ""}{change} lbs</p></div>
        </div>
        <div className="h-40 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={weightData}>
              <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis hide domain={["dataMin - 2", "dataMax + 2"]} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #333", borderRadius: 8 }} labelStyle={{ color: "#A1A1AA" }} />
              <Line type="monotone" dataKey="value" stroke="#FF3B30" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-2 mt-3">
          <input data-testid="weight-input" type="number" placeholder="Log weight (lbs)" value={newWeight} onChange={(e) => setNewWeight(e.target.value)}
            className="flex-1 bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
          <button data-testid="weight-add" onClick={addWeight} className="px-4 bg-brand text-white rounded-lg font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Log</button>
        </div>
      </div>

      <h3 className="text-lg mt-6">Achievements</h3>
      <div className="grid grid-cols-3 gap-3 mt-3">
        {ACHIEVEMENTS.map((a, i) => (
          <div key={i} className="card-surface rounded-xl p-3 text-center">
            <div className="w-10 h-10 mx-auto rounded-full bg-success/15 flex items-center justify-center"><a.icon className="w-5 h-5 text-success" /></div>
            <p className="text-[11px] text-secondary mt-2 leading-tight">{a.label}</p>
          </div>
        ))}
      </div>

      <h3 className="text-lg mt-6">Goals</h3>
      <div className="space-y-3 mt-3">
        {goals.map((g) => (
          <div key={g.id} className="card-surface rounded-xl p-4" data-testid="goal-item">
            <div className="flex items-center justify-between"><p className="font-semibold">{g.title}</p><span className="font-num text-sm text-brand">{g.progress}%</span></div>
            <div className="h-2 bg-white/10 rounded-full mt-2"><div className="h-full bg-brand rounded-full" style={{ width: `${g.progress}%` }} /></div>
            <p className="text-xs text-secondary mt-2">{g.category} · Target by {g.target_date}</p>
          </div>
        ))}
        {goals.length === 0 && <p className="text-tertiary text-sm">No goals yet.</p>}
      </div>
    </div>
  );
}
